/**
 * UsageTab — вкладка «Расход» карточки клиента: сколько клиника съела
 * токенов и сообщений WhatsApp за месяц и во что это обходится по ставкам.
 * Ниже — по дням (дни без активности бэк не присылает).
 */

import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import dayjs from 'dayjs';

import {clinicUsageQueryOptions, paidMessages} from '@services/usage';
import {Loader} from '@components/ui/Loader';
import {UsageTotalsGrid} from '@components/UsageTotalsGrid';
import {cn} from '@lib/utils';
import {formatCompact, formatNumber} from '@utils/format';

import {SectionCard} from '../SectionCard';

type Props = {clientId: string};

export const UsageTab = ({clientId}: Props) => {
  /** 0 — текущий месяц, 1 — прошлый */
  const [back, setBack] = useState(0);
  const month = dayjs().subtract(back, 'month');
  const from = month.startOf('month').format('YYYY-MM-DD');
  const to = (back === 0 ? dayjs() : month.endOf('month')).format('YYYY-MM-DD');
  const {data, isLoading} = useQuery(clinicUsageQueryOptions(clientId, from, to));

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex rounded-[10px] border border-skr-line bg-card p-[3px]">
        {[0, 1].map((b) => (
          <button
            key={b}
            onClick={() => setBack(b)}
            className={cn(
              'flex-1 rounded-[8px] py-1.5 text-[12.5px] font-medium capitalize text-muted-foreground transition',
              back === b && 'bg-skr-head font-semibold text-foreground',
            )}
          >
            {dayjs().subtract(b, 'month').format('MMMM YYYY')}
          </button>
        ))}
      </div>

      {isLoading || !data ? (
        <div className="flex min-h-[220px] items-center justify-center">
          <Loader label="Считаем расход…" />
        </div>
      ) : (
        <>
          <UsageTotalsGrid
            totals={data.totals}
            costTiyin={data.estimatedCostTiyin}
            rateCard={data.rateCard}
          />
          <SectionCard title="По дням">
            {data.days.length === 0 ? (
              <div className="py-4 text-center text-[13px] text-muted-foreground">
                За месяц активности не было
              </div>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-[11.5px] text-muted-foreground">
                    <th className="pb-1.5 font-medium">День</th>
                    <th className="pb-1.5 text-right font-medium">Диалоги</th>
                    <th className="pb-1.5 text-right font-medium">Токены</th>
                    <th className="pb-1.5 text-right font-medium">Голос</th>
                    <th className="pb-1.5 text-right font-medium">Платные WA</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {data.days.map((d) => (
                    <tr key={d.day} className="border-t border-skr-hairline">
                      <td className="py-1.5 font-sans">{dayjs(d.day).format('D MMM, dd')}</td>
                      <td className="py-1.5 text-right">{formatNumber(d.totals.dialogs)}</td>
                      <td className="py-1.5 text-right">
                        {formatCompact(d.totals.inputTokens + d.totals.outputTokens)}
                      </td>
                      <td className="py-1.5 text-right">{formatNumber(d.totals.voiceCalls)}</td>
                      <td className="py-1.5 text-right">{formatNumber(paidMessages(d.totals))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
};
